import React, { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { founderAdminAuth, founderAdminFunctions, normalizePhone } from '../services/firebase';
import { CheckCircle2, XCircle, AlertTriangle, ShieldCheck, Clock, Lock, RefreshCw, Send } from 'lucide-react';

interface FboRequestItem {
  id: string;
  status: 'pending_approval' | 'processing' | 'executed' | 'rejected' | 'expired' | 'queued_for_platform_limit' | 'failed';
  amount: number;
  recipientPhone: string;
  recipientName?: string | null;
  reason: string;
  notes?: string | null;
  requestedByUid: string;
  requestedByPhone?: string | null;
  requestedByEmail?: string | null;
  requestedByName?: string | null;
  approvedByUid?: string | null;
  approvedByName?: string | null;
  approvedByEmail?: string | null;
  rejectedByUid?: string | null;
  rejectionReason?: string | null;
  directPaymentId?: string | null;
  astraTransferId?: string | null;
  createdAt?: any;
  expiresAt?: any;
}

interface Props {
  founderPhone: string;
}

const STORAGE_KEY = 'clero_fbo_pending_draft';

interface SavedDraft {
  clientRequestKey: string;
  recipientPhone: string;
  recipientName: string;
  amount: string;
  reason: string;
  notes: string;
  overrideRequestKey?: string | null;
  duplicatePrompt?: { requestId: string; status: string } | null;
}

const FboDisbursementAdminPanel: React.FC<Props> = ({ founderPhone }) => {
  const [activeTab, setActiveTab] = useState<'pending' | 'new_request' | 'history'>('pending');
  const [requests, setRequests] = useState<FboRequestItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState('');
  const [isKeyConflict, setIsKeyConflict] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const [savedDraft] = useState<SavedDraft | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const [clientRequestKey, setClientRequestKey] = useState(
    () => savedDraft?.clientRequestKey || ('fbo_req_' + Math.random().toString(36).substring(2) + '_' + Date.now())
  );
  const [overrideRequestKey, setOverrideRequestKey] = useState<string | null>(
    () => savedDraft?.overrideRequestKey || null
  );
  const [duplicatePrompt, setDuplicatePrompt] = useState<{ requestId: string; status: string } | null>(
    () => savedDraft?.duplicatePrompt || null
  );

  // New Request Form State (retained across reloads)
  const [recipientPhone, setRecipientPhone] = useState(() => savedDraft?.recipientPhone || '');
  const [recipientName, setRecipientName] = useState(() => savedDraft?.recipientName || '');
  const [amount, setAmount] = useState(() => savedDraft?.amount || '');
  const [reason, setReason] = useState(() => savedDraft?.reason || '');
  const [notes, setNotes] = useState(() => savedDraft?.notes || '');
  const [submitting, setSubmitting] = useState(false);

  // Persist uncommitted draft and key across page reloads
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (recipientPhone || amount || reason) {
        sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            clientRequestKey,
            recipientPhone,
            recipientName,
            amount,
            reason,
            notes,
            overrideRequestKey,
            duplicatePrompt,
          })
        );
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch {}
  }, [clientRequestKey, recipientPhone, recipientName, amount, reason, notes, overrideRequestKey, duplicatePrompt]);

  // Approval Modal State
  const [selectedRequest, setSelectedRequest] = useState<FboRequestItem | null>(null);
  const [passphrase, setPassphrase] = useState('');
  const [approving, setApproving] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [actionError, setActionError] = useState('');

  const currentUid = founderAdminAuth.currentUser?.uid || '';

  const loadRequests = async (statusFilter?: string) => {
    setLoading(true);
    setError('');
    try {
      const listFn = httpsCallable<{ status?: string; limit?: number }, { ok: boolean; requests: FboRequestItem[] }>(
        founderAdminFunctions,
        'listFboDisbursementRequests'
      );
      const res = await listFn({ status: statusFilter || undefined, limit: 50 });
      setRequests(res.data?.requests || []);
      setLoadFailed(false);
    } catch (err: any) {
      setError(err?.message || 'Failed to load FBO disbursement requests.');
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'pending') {
      loadRequests('pending_approval');
    } else if (activeTab === 'history') {
      loadRequests();
    }
  }, [activeTab]);

  const handleCreateRequest = async (e?: React.FormEvent, options?: { allowDuplicatePayment?: boolean }) => {
    if (e) e.preventDefault();
    setError('');
    setSuccessMsg('');
    const parsedAmount = parseFloat(amount);
    const cleanPhone = normalizePhone(recipientPhone);

    if (!cleanPhone) {
      setError('Please provide a valid 10-digit recipient phone number.');
      return;
    }
    if (isNaN(parsedAmount) || parsedAmount <= 0 || parsedAmount > 500) {
      setError('Amount must be between $0.01 and $500.00.');
      return;
    }
    if (!reason.trim()) {
      setError('A business justification reason is required.');
      return;
    }

    setSubmitting(true);
    try {
      const isCreateAnother = Boolean(options?.allowDuplicatePayment || overrideRequestKey);
      let effectiveKey = clientRequestKey;
      if (isCreateAnother) {
        if (!overrideRequestKey) {
          const generatedOverrideKey = 'fbo_req_' + Math.random().toString(36).substring(2) + '_' + Date.now();
          setOverrideRequestKey(generatedOverrideKey);
          effectiveKey = generatedOverrideKey;
        } else {
          effectiveKey = overrideRequestKey;
        }
      }

      const requestFn = httpsCallable<any, any>(founderAdminFunctions, 'requestFboDisbursement');
      const res = await requestFn({
        recipientPhone: cleanPhone,
        recipientName: recipientName.trim() || undefined,
        amount: parsedAmount,
        reason: reason.trim(),
        notes: notes.trim() || undefined,
        clientRequestKey: effectiveKey,
        allowDuplicatePayment: isCreateAnother,
      });

      const notifDelivered = res.data?.notificationStatus === 'delivered';
      const notifNote = notifDelivered
        ? 'Founders notified at founders@clero.money.'
        : 'Founder email notification delivery pending/queued.';

      if (res.data?.deduplicated) {
        if (res.data?.duplicateReason === 'recent_identical_payment' && !isCreateAnother) {
          setDuplicatePrompt({
            requestId: res.data.requestId,
            status: res.data.status,
          });
          return;
        }

        if (res.data?.status === 'pending_approval') {
          setSuccessMsg(`Disbursement request is already pending approval (ID: ${res.data?.requestId}). ${notifNote}`);
        } else {
          setError(`A disbursement request with this request key already exists with status "${res.data?.status}" (ID: ${res.data?.requestId}). Please start a new request if needed.`);
          setClientRequestKey('fbo_req_' + Math.random().toString(36).substring(2) + '_' + Date.now());
          setOverrideRequestKey(null);
          return;
        }
      } else {
        setSuccessMsg(`Disbursement request created successfully (ID: ${res.data?.requestId}). ${notifNote}`);
      }
      setDuplicatePrompt(null);
      setOverrideRequestKey(null);
      setClientRequestKey('fbo_req_' + Math.random().toString(36).substring(2) + '_' + Date.now());
      setRecipientPhone('');
      setRecipientName('');
      setAmount('');
      setReason('');
      setNotes('');
      if (typeof window !== 'undefined') {
        try {
          sessionStorage.removeItem(STORAGE_KEY);
        } catch {}
      }
      setIsKeyConflict(false);
      setActiveTab('pending');
    } catch (err: any) {
      const msg = err?.message || 'Failed to submit disbursement request.';
      setError(msg);
      if (msg.includes('already submitted with different details') || err?.code === 'already-exists') {
        setIsKeyConflict(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    if (!passphrase.trim()) {
      setActionError('Authorization secret passphrase is required.');
      return;
    }

    setApproving(true);
    setActionError('');
    try {
      const approveFn = httpsCallable<any, any>(founderAdminFunctions, 'approveFboDisbursement');
      const res = await approveFn({
        requestId: selectedRequest.id,
        passphrase: passphrase.trim(),
      });

      if (res.data?.status === 'queued_for_platform_limit') {
        setSuccessMsg(
          `Disbursement of $${selectedRequest.amount.toFixed(2)} queued: daily platform limit reached. It will be executed when the limit window resets.`
        );
      } else {
        setSuccessMsg(`Disbursement of $${selectedRequest.amount.toFixed(2)} executed successfully! Astra payout initiated.`);
      }
      setSelectedRequest(null);
      setPassphrase('');
      loadRequests('pending_approval');
    } catch (err: any) {
      setActionError(err?.message || 'Failed to approve disbursement.');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    setRejecting(true);
    setActionError('');
    try {
      const rejectFn = httpsCallable<any, any>(founderAdminFunctions, 'rejectFboDisbursement');
      await rejectFn({
        requestId: selectedRequest.id,
        reason: rejectReason.trim() || 'Rejected by founder',
      });

      setSuccessMsg(`Disbursement request ${selectedRequest.id} was rejected.`);
      setSelectedRequest(null);
      setRejectReason('');
      loadRequests('pending_approval');
    } catch (err: any) {
      setActionError(err?.message || 'Failed to reject disbursement.');
    } finally {
      setRejecting(false);
    }
  };

  const pendingCount = requests.filter(r => r.status === 'pending_approval').length;

  return (
    <div className="mb-8 rounded-[32px] border border-slate-200 bg-white p-6 md:p-8 shadow-[0_18px_60px_rgba(15,23,42,0.08)]">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-6">
        <div>
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
              <ShieldCheck className="h-5 w-5" />
            </span>
            <h2 className="text-xl font-bold text-slate-900">FBO Disbursements & Refunds</h2>
            <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800">
              Dual-Control
            </span>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            Secure disbursements from Clero's master FBO account. Requires Maker request + Checker signoff from an authorized founder.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 rounded-2xl bg-slate-100 p-1 text-sm font-medium">
          <button
            onClick={() => setActiveTab('pending')}
            className={`flex items-center gap-1.5 rounded-xl px-4 py-2 transition-colors ${
              activeTab === 'pending'
                ? 'bg-white text-slate-900 shadow-sm font-semibold'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Clock className="h-4 w-4" />
            Pending Signoffs
            {!loadFailed && pendingCount > 0 ? (
              <span className="ml-1 rounded-full border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-[11px] font-bold text-amber-950">
                {pendingCount}
              </span>
            ) : null}
          </button>
          <button
            onClick={() => setActiveTab('new_request')}
            className={`flex items-center gap-1.5 rounded-xl px-4 py-2 transition-colors ${
              activeTab === 'new_request'
                ? 'bg-emerald-600 text-white shadow-sm font-bold'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Send className="h-4 w-4" />
            ＋ Create Refund
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex items-center gap-1.5 rounded-xl px-4 py-2 transition-colors ${
              activeTab === 'history'
                ? 'bg-white text-slate-900 shadow-sm font-semibold'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Audit History
          </button>
        </div>
      </div>

      {/* Notifications */}
      {error ? (
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-2xl bg-rose-50 p-4 text-sm text-rose-700">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 text-rose-500" />
            <span>{error}</span>
          </div>
          {isKeyConflict && (
            <button
              type="button"
              onClick={() => {
                setClientRequestKey('fbo_req_' + Math.random().toString(36).substring(2) + '_' + Date.now());
                setOverrideRequestKey(null);
                setDuplicatePrompt(null);
                setIsKeyConflict(false);
                setError('');
              }}
              className="flex-shrink-0 rounded-xl bg-rose-100 px-3 py-1.5 text-xs font-semibold text-rose-800 hover:bg-rose-200 transition-colors"
            >
              Assign New Key For Edited Details
            </button>
          )}
        </div>
      ) : null}

      {successMsg ? (
        <div className="mt-4 flex items-center gap-2 rounded-2xl bg-emerald-50 p-4 text-sm text-emerald-800">
          <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-emerald-600" />
          <span>{successMsg}</span>
        </div>
      ) : null}

      {/* Content */}
      <div className="mt-6">
        {/* NEW REQUEST FORM */}
        {activeTab === 'new_request' && (
          <form onSubmit={handleCreateRequest} className="max-w-2xl space-y-4">
            <div className="border-b border-slate-100 pb-3">
              <h3 className="text-base font-bold text-slate-900">Create Clero Refund / FBO Payout</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Submits a payment request from Clero's FBO account. An authorized second founder must sign off with the master passphrase to execute the ACH transfer.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600">
                  Recipient Phone *
                </label>
                <input
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  value={recipientPhone}
                  onChange={(e) => setRecipientPhone(e.target.value)}
                  required
                  className="mt-1.5 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-emerald-500 focus:bg-white transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600">
                  Amount ($ Max 500.00) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  max="500"
                  min="0.01"
                  placeholder="25.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                  className="mt-1.5 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-emerald-500 focus:bg-white transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600">
                Recipient Name (Optional)
              </label>
              <input
                type="text"
                placeholder="Jane Doe"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                className="mt-1.5 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-emerald-500 focus:bg-white transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600">
                Business Justification Reason *
              </label>
              <input
                type="text"
                placeholder="Customer dispute resolution for Session #1234"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                className="mt-1.5 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-emerald-500 focus:bg-white transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600">
                Internal Notes (Optional)
              </label>
              <textarea
                placeholder="Context or ticket reference numbers..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="mt-1.5 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2 text-sm text-slate-900 outline-none focus:border-emerald-500 focus:bg-white transition-all"
              />
            </div>

            {duplicatePrompt && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900">
                <div className="font-semibold text-sm">Recent Identical Disbursement Found</div>
                <p className="mt-1 text-slate-700">
                  An identical disbursement was already requested in the last 15 minutes (ID:{' '}
                  <code className="bg-amber-100 px-1 py-0.5 rounded font-mono font-semibold text-amber-900">
                    {duplicatePrompt.requestId}
                  </code>
                  , Status: <strong>{duplicatePrompt.status}</strong>).
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <button
                    type="button"
                    disabled={submitting}
                    onClick={() => handleCreateRequest(undefined, { allowDuplicatePayment: true })}
                    className="rounded-xl bg-amber-600 px-3.5 py-2 font-bold text-white hover:bg-amber-700 transition"
                  >
                    Create Another Request Anyway
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setDuplicatePrompt(null);
                      setOverrideRequestKey(null);
                      setClientRequestKey('fbo_req_' + Math.random().toString(36).substring(2) + '_' + Date.now());
                      setRecipientPhone('');
                      setRecipientName('');
                      setAmount('');
                      setReason('');
                      setNotes('');
                      if (typeof window !== 'undefined') {
                        try {
                          sessionStorage.removeItem(STORAGE_KEY);
                        } catch {}
                      }
                    }}
                    className="rounded-xl border border-amber-300 bg-white px-3 py-2 font-semibold text-amber-800 hover:bg-amber-50 transition"
                  >
                    Discard Form
                  </button>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-6 py-3 text-sm font-bold text-white shadow-md hover:bg-emerald-500 transition-all disabled:opacity-50"
              >
                {submitting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {error ? 'Retry Disbursement Request' : 'Submit Disbursement Request'}
              </button>

              {(recipientPhone || amount || reason || notes) && (
                <button
                  type="button"
                  onClick={() => {
                    setDuplicatePrompt(null);
                    setOverrideRequestKey(null);
                    setClientRequestKey('fbo_req_' + Math.random().toString(36).substring(2) + '_' + Date.now());
                    setRecipientPhone('');
                    setRecipientName('');
                    setAmount('');
                    setReason('');
                    setNotes('');
                    setError('');
                    setSuccessMsg('');
                    if (typeof window !== 'undefined') {
                      try {
                        sessionStorage.removeItem(STORAGE_KEY);
                      } catch {}
                    }
                  }}
                  disabled={submitting}
                  className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  Discard Draft
                </button>
              )}
            </div>
          </form>
        )}

        {/* PENDING APPROVALS LIST */}
        {activeTab === 'pending' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-slate-700">
                Awaiting Signoff {loadFailed ? '' : `(${requests.length})`}
              </h3>
              <button
                onClick={() => loadRequests('pending_approval')}
                className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {loadFailed ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center text-sm text-rose-700">
                <p className="font-semibold">Failed to load pending disbursement requests.</p>
                <p className="mt-1 text-xs text-rose-600">{error || 'Network error occurred while fetching requests.'}</p>
                <button
                  type="button"
                  onClick={() => loadRequests('pending_approval')}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-rose-600 px-4 py-2 text-xs font-bold text-white hover:bg-rose-500 shadow-sm transition-all"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Try Again
                </button>
              </div>
            ) : requests.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500">
                <p>No pending disbursement requests awaiting approval.</p>
                <button
                  type="button"
                  onClick={() => setActiveTab('new_request')}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-500 shadow-sm transition-all"
                >
                  <Send className="h-3.5 w-3.5" />
                  ＋ Create New Refund Request
                </button>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 overflow-hidden">
                {requests.map((req) => {
                  const isRequester = req.requestedByUid === currentUid;
                  return (
                    <div key={req.id} className="flex flex-col md:flex-row md:items-center justify-between p-4 gap-4 bg-white hover:bg-slate-50 transition-colors">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-base font-bold text-slate-950">${req.amount.toFixed(2)}</span>
                          <span className="text-sm text-slate-500">→ {req.recipientPhone}</span>
                          {req.recipientName ? <span className="text-xs text-slate-400">({req.recipientName})</span> : null}
                        </div>
                        <p className="text-xs text-slate-600 mt-1">
                          <strong>Reason:</strong> {req.reason}
                        </p>
                        <p className="text-[11px] text-slate-400 mt-0.5">
                          Requested by: <strong>{req.requestedByName || req.requestedByEmail || 'Admin'}</strong>
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        {isRequester ? (
                          <span className="text-xs font-medium text-amber-700 bg-amber-50 px-3 py-1.5 rounded-xl border border-amber-200">
                            Awaiting Second Founder Signoff
                          </span>
                        ) : (
                          <button
                            onClick={() => {
                              setSelectedRequest(req);
                              setActionError('');
                              setPassphrase('');
                            }}
                            className="flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-500 shadow-sm transition-all"
                          >
                            <Lock className="h-3.5 w-3.5" />
                            Review & Sign Off
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* AUDIT LOG */}
        {activeTab === 'history' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-slate-700">Recent Disbursements</h3>
              <button
                onClick={() => loadRequests()}
                className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {requests.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500">
                No past disbursement records found.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-slate-200">
                <table className="w-full text-left text-xs text-slate-600">
                  <thead className="bg-slate-50 text-[11px] font-bold uppercase text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Amount</th>
                      <th className="px-4 py-3">Recipient</th>
                      <th className="px-4 py-3">Reason</th>
                      <th className="px-4 py-3">Maker</th>
                      <th className="px-4 py-3">Checker/Founder</th>
                      <th className="px-4 py-3">Astra Transfer ID</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {requests.map((r) => (
                      <tr key={r.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-semibold">
                          <span
                            className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                              r.status === 'executed'
                                ? 'bg-emerald-100 text-emerald-800'
                                : r.status === 'rejected'
                                ? 'bg-rose-100 text-rose-800'
                                : r.status === 'pending_approval'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-slate-100 text-slate-700'
                            }`}
                          >
                            {r.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-bold text-slate-900">${r.amount.toFixed(2)}</td>
                        <td className="px-4 py-3">{r.recipientPhone}</td>
                        <td className="px-4 py-3">{r.reason}</td>
                        <td className="px-4 py-3">{r.requestedByName || r.requestedByEmail || 'Admin'}</td>
                        <td className="px-4 py-3">{r.approvedByName || r.approvedByEmail || '-'}</td>
                        <td className="px-4 py-3 font-mono text-[10px] text-slate-400">{r.astraTransferId || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* APPROVAL / REJECTION MODAL */}
      {selectedRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 backdrop-blur-sm p-4">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 md:p-8 shadow-2xl border border-slate-100 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-emerald-600" />
                <h3 className="text-lg font-bold text-slate-900">Founder Signoff Authorization</h3>
              </div>
              <button
                onClick={() => setSelectedRequest(null)}
                className="text-slate-400 hover:text-slate-600 text-sm font-semibold"
              >
                ✕
              </button>
            </div>

            <div className="mt-4 rounded-2xl bg-slate-50 p-4 text-xs text-slate-600 space-y-1.5 border border-slate-100">
              <div className="flex justify-between">
                <span className="text-slate-400">Amount:</span>
                <strong className="text-slate-900 text-sm">${selectedRequest.amount.toFixed(2)}</strong>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Recipient Phone:</span>
                <strong className="text-slate-900">{selectedRequest.recipientPhone}</strong>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Requested By:</span>
                <strong className="text-slate-900">{selectedRequest.requestedByName || selectedRequest.requestedByEmail}</strong>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Reason:</span>
                <strong className="text-slate-900">{selectedRequest.reason}</strong>
              </div>
            </div>

            {actionError ? (
              <div className="mt-4 flex items-center gap-2 rounded-xl bg-rose-50 p-3 text-xs text-rose-700">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 text-rose-500" />
                <span>{actionError}</span>
              </div>
            ) : null}

            {/* Passphrase Input */}
            <div className="mt-5">
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700">
                Enter Master Authorization Passphrase *
              </label>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Must be signed off by a designated authorized founder.
              </p>
              <input
                type="password"
                placeholder="Enter passphrase..."
                value={passphrase}
                onChange={(e) => setPassphrase(e.target.value)}
                className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-emerald-500 transition-all font-mono"
              />
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={handleApprove}
                disabled={approving || rejecting}
                className="flex-1 flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 py-3 text-xs font-bold text-white shadow-md hover:bg-emerald-500 transition-all disabled:opacity-50"
              >
                {approving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                Authorize & Release Funds
              </button>
              <button
                type="button"
                onClick={handleReject}
                disabled={approving || rejecting}
                className="flex items-center justify-center gap-1.5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-bold text-rose-700 hover:bg-rose-100 transition-all disabled:opacity-50"
              >
                {rejecting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FboDisbursementAdminPanel;
